import React from 'react';
import { AddressAutocomplete } from '../common';

function LocationCommunityForm({ 
  formData, 
  setFormData, 
  isEditing,
  validationErrors = {},
  setValidationErrors = () => {}
}) {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear validation error when user starts typing
    if (validationErrors[name] || validationErrors[name.split('.').pop()]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: '',
        [name.split('.').pop()]: ''
      }));
    }
  };

  const handleNestedArrayInputChange = (parent, field, value, isRaw = false) => {
    if (isRaw) {
      // Store raw text for typing experience
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [`${field}Raw`]: value,
          [field]: value.split(',').map(item => item.trim()).filter(item => item)
        }
      }));
    } else {
      // Clean up the raw text (used on blur)
      const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [`${field}Raw`]: arrayValue.join(', '),
          [field]: arrayValue
        }
      }));
    }

    // Clear validation error
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Special handler for ZIP codes that preserves typing experience
  const handleZipCodesInputChange = (value) => {
    // Store the raw text for display
    setFormData(prev => ({
      ...prev,
      serviceAreas: {
        ...prev.serviceAreas,
        zipCodesRaw: value, // Store raw text for typing experience
        zipCodes: value.split(',').map(item => item.trim()).filter(item => item).slice(0, 5)
      }
    }));

    // Clear validation error
    if (validationErrors.zipCodes) {
      setValidationErrors(prev => ({
        ...prev,
        zipCodes: ''
      }));
    }
  };

  // Handler for when an address is selected from autocomplete
  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        address: addressData.address,
        city: addressData.city,
        state: addressData.state,
        zipCode: addressData.zipCode,
        coordinates: addressData.coordinates
      }
    }));

    // Clear all address-related validation errors
    setValidationErrors(prev => ({
      ...prev,
      address: '',
      city: '',
      state: '',
      zipCode: '',
      'location.address': '',
      'location.city': '',
      'location.state': '',
      'location.zipCode': ''
    }));
  };

  // Validation helper component
  const RequiredIndicator = () => (
    <span className="text-red-500 font-medium ml-1" title="Required field">*</span>
  );

  const ValidationError = ({ error }) => {
    if (!error) return null;
    return (
      <div className="mt-1 flex items-center gap-1">
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-600 text-sm font-medium">{error}</p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Location */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Location & Service Areas
          <RequiredIndicator />
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Practice Address
              <RequiredIndicator />
            </label>
            <p className="text-xs text-[#254e70]/60 mb-2">
              Enter your main practice location where you see clients
            </p>
            {isEditing ? (
              <div>
                <AddressAutocomplete
                  value={formData.location.address}
                  onChange={handleInputChange}
                  onPlaceSelect={handleAddressSelect}
                  placeholder="Start typing your practice address..."
                  disabled={!isEditing}
                  className={validationErrors.address ? 'border-red-500' : ''}
                />
                <ValidationError error={validationErrors.address} />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80">{formData.location.address || 'Not set'}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                City
                <RequiredIndicator />
              </label>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    name="location.city"
                    value={formData.location.city}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.city ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="City name"
                  />
                  <ValidationError error={validationErrors.city} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.location.city || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                State
                <RequiredIndicator />
              </label>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    name="location.state"
                    value={formData.location.state}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.state ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="State (e.g., CA)"
                  />
                  <ValidationError error={validationErrors.state} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.location.state || 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#254e70] mb-1">
                ZIP Code
                <RequiredIndicator />
              </label>
              {isEditing ? (
                <div>
                  <input
                    type="text"
                    name="location.zipCode"
                    value={formData.location.zipCode}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] ${
                      validationErrors.zipCode ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                    }`}
                    placeholder="12345"
                  />
                  <ValidationError error={validationErrors.zipCode} />
                </div>
              ) : (
                <p className="py-2 text-[#254e70]/80">{formData.location.zipCode || 'Not set'}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Service Type
              <RequiredIndicator />
            </label>
            <p className="text-xs text-[#254e70]/60 mb-3">
              Select how you provide therapy services to clients
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="serviceAreas.virtualOnly"
                  checked={formData.serviceAreas.virtualOnly}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Virtual Only</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="serviceAreas.inPersonOnly"
                  checked={formData.serviceAreas.inPersonOnly}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">In-Person Only</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="serviceAreas.hybrid"
                  checked={formData.serviceAreas.hybrid}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Hybrid (Both)</label>
              </div>
            </div>
            <ValidationError error={validationErrors.serviceType} />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">
              Additional ZIP Codes Served
              <span className="text-xs text-[#254e70]/60 ml-1">(up to 5, optional)</span>
            </label>
            <p className="text-xs text-[#254e70]/60 mb-2">
              Add ZIP codes where you provide services beyond your main practice location
            </p>
            {isEditing ? (
              <div>
                <textarea
                  name="serviceAreas.zipCodes"
                  value={formData.serviceAreas.zipCodesRaw || formData.serviceAreas.zipCodes.join(', ')}
                  onChange={(e) => handleZipCodesInputChange(e.target.value)}
                  placeholder="98101, 98102, 98103, etc. (comma-separated, max 5)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] h-16 ${
                    validationErrors.zipCodes ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                />
                <div className="flex justify-between items-start mt-1">
                  <ValidationError error={validationErrors.zipCodes} />
                  <p className="text-xs text-[#254e70]/60">
                    Enter up to 5 ZIP codes, separated by commas
                  </p>
                </div>
                {formData.serviceAreas.zipCodes.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-[#254e70]/60 mb-1">Currently serving {formData.serviceAreas.zipCodes.length}/5 ZIP codes:</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.serviceAreas.zipCodes.map((zip, index) => (
                        <span key={index} className="inline-block px-2 py-1 bg-[#9dacff]/10 text-[#254e70] rounded text-xs">
                          {zip}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="py-2 text-[#254e70]/80">
                  {formData.serviceAreas.zipCodes.length > 0 
                    ? formData.serviceAreas.zipCodes.join(', ') 
                    : 'None specified'
                  }
                </p>
                {formData.serviceAreas.zipCodes.length > 0 && (
                  <p className="text-xs text-[#254e70]/60">
                    Serving {formData.serviceAreas.zipCodes.length} additional ZIP code{formData.serviceAreas.zipCodes.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Community & Cultural Focus */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#254e70]/10">
        <h3 className="text-base font-semibold text-[#254e70] mb-3" style={{ fontFamily: 'Kanit, sans-serif' }}>
          Community & Cultural Focus
          <span className="text-sm font-normal text-[#254e70]/60 ml-2">(Optional)</span>
        </h3>
        <p className="text-sm text-[#254e70]/70 mb-4">
          Help clients find therapists who understand their identity and experiences
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-2">
              Community Affirmations
            </label>
            <p className="text-xs text-[#254e70]/60 mb-3">
              Select the communities and approaches you specialize in or are allied with
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="communityFocus.lgbtqAllied"
                  checked={formData.communityFocus.lgbtqAllied}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">LGBTQ+ Allied</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="communityFocus.racialJusticeAllied"
                  checked={formData.communityFocus.racialJusticeAllied}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Racial Justice Allied</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="communityFocus.bodyPositive"
                  checked={formData.communityFocus.bodyPositive}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Body Positive</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="communityFocus.traumaInformed"
                  checked={formData.communityFocus.traumaInformed}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Trauma-Informed</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="communityFocus.neurodiversityAffirming"
                  checked={formData.communityFocus.neurodiversityAffirming}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Neurodiversity Affirming</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="communityFocus.faithBased"
                  checked={formData.communityFocus.faithBased}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mr-2 h-4 w-4 text-[#254e70] focus:ring-[#254e70] border-gray-300 rounded"
                />
                <label className="text-sm text-[#254e70]">Faith-Based</label>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">Religious Affiliations</label>
            <p className="text-xs text-[#254e70]/60 mb-2">
              List religious or spiritual traditions you work with or practice
            </p>
            {isEditing ? (
              <div>
                <textarea
                  name="communityFocus.religiousAffiliations"
                  value={formData.communityFocus.religiousAffiliationsRaw || formData.communityFocus.religiousAffiliations.join(', ')}
                  onChange={(e) => handleNestedArrayInputChange('communityFocus', 'religiousAffiliations', e.target.value, true)}
                  onBlur={(e) => handleNestedArrayInputChange('communityFocus', 'religiousAffiliations', e.target.value, false)}
                  placeholder="Christian, Jewish, Muslim, Buddhist, etc. (comma-separated)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] h-16 ${
                    validationErrors.religiousAffiliations ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationError error={validationErrors.religiousAffiliations} />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80">
                {formData.communityFocus.religiousAffiliations.length > 0 
                  ? formData.communityFocus.religiousAffiliations.join(', ') 
                  : 'None specified'
                }
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">Cultural Specialties</label>
            <p className="text-xs text-[#254e70]/60 mb-2">
              Cultural communities or identities you have specialized experience with
            </p>
            {isEditing ? (
              <div>
                <textarea
                  name="communityFocus.culturalSpecialties"
                  value={formData.communityFocus.culturalSpecialtiesRaw || formData.communityFocus.culturalSpecialties.join(', ')}
                  onChange={(e) => handleNestedArrayInputChange('communityFocus', 'culturalSpecialties', e.target.value, true)}
                  onBlur={(e) => handleNestedArrayInputChange('communityFocus', 'culturalSpecialties', e.target.value, false)}
                  placeholder="Asian American, Latino/Hispanic, African American, etc. (comma-separated)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] h-16 ${
                    validationErrors.culturalSpecialties ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationError error={validationErrors.culturalSpecialties} />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80">
                {formData.communityFocus.culturalSpecialties.length > 0 
                  ? formData.communityFocus.culturalSpecialties.join(', ') 
                  : 'None specified'
                }
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#254e70] mb-1">Accessibility Features</label>
            <p className="text-xs text-[#254e70]/60 mb-2">
              Accessibility accommodations available at your practice
            </p>
            {isEditing ? (
              <div>
                <textarea
                  name="communityFocus.accessibilityFeatures"
                  value={formData.communityFocus.accessibilityFeaturesRaw || formData.communityFocus.accessibilityFeatures.join(', ')}
                  onChange={(e) => handleNestedArrayInputChange('communityFocus', 'accessibilityFeatures', e.target.value, true)}
                  onBlur={(e) => handleNestedArrayInputChange('communityFocus', 'accessibilityFeatures', e.target.value, false)}
                  placeholder="Wheelchair accessible, ASL interpretation available, etc. (comma-separated)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-[#9dacff] h-16 ${
                    validationErrors.accessibilityFeatures ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'
                  }`}
                />
                <ValidationError error={validationErrors.accessibilityFeatures} />
              </div>
            ) : (
              <p className="py-2 text-[#254e70]/80">
                {formData.communityFocus.accessibilityFeatures.length > 0 
                  ? formData.communityFocus.accessibilityFeatures.join(', ') 
                  : 'None specified'
                }
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationCommunityForm; 